import type { FC } from 'react'
import type { SxProps, Theme } from '@mui/material'
import { Box, Typography, useTheme } from '@mui/material'
import { EventType } from '@/features/events/shared/types'
import { ToolHeader } from '../shared'
import { infoContainerSx, infoRowSx, labelSx, pathValueSx, patternValueSx } from './shared'

interface GrepContextRendererProps {
  readonly pattern: string
  readonly path?: string | undefined
  readonly glob?: string | undefined
  readonly type?: string | undefined
  readonly output_mode?: string | undefined
  readonly '-n'?: boolean | undefined
  readonly '-i'?: boolean | undefined
  readonly '-C'?: number | undefined
  readonly '-A'?: number | undefined
  readonly '-B'?: number | undefined
  readonly head_limit?: number | undefined
  readonly multiline?: boolean | undefined
}

export const GrepContextRenderer: FC<GrepContextRendererProps> = ({
  pattern,
  path,
  glob,
  type,
  output_mode,
  '-n': showLineNumbers,
  '-i': caseInsensitive,
  '-C': contextLines,
  '-A': afterContextLines,
  '-B': beforeContextLines,
  head_limit,
  multiline,
}) => {
  const theme = useTheme()

  const args = buildGrepArgumentsList({
    caseInsensitive,
    showLineNumbers,
    contextLines,
    afterContextLines,
    beforeContextLines,
    multiline,
    output_mode,
    head_limit,
  })

  return (
    <>
      <ToolHeader eventType={EventType.TOOL_PERMISSION_REQUEST} toolName="Grep" />

      <Box sx={infoContainerSx}>
        <Box sx={infoRowSx}>
          <Typography variant="caption" sx={labelSx}>
            Pattern:
          </Typography>
          <Typography variant="body2" sx={patternValueSx(theme)}>
            {pattern}
          </Typography>
        </Box>

        {type && (
          <Box sx={infoRowSx}>
            <Typography variant="caption" sx={labelSx}>
              File type:
            </Typography>
            <Typography variant="body2" sx={patternValueSx(theme)}>
              {type}
            </Typography>
          </Box>
        )}

        {glob && (
          <Box sx={infoRowSx}>
            <Typography variant="caption" sx={labelSx}>
              Filter:
            </Typography>
            <Typography variant="body2" sx={patternValueSx(theme)}>
              {glob}
            </Typography>
          </Box>
        )}

        {path && (
          <Box sx={infoRowSx}>
            <Typography variant="caption" sx={labelSx}>
              Search path:
            </Typography>
            <Typography variant="body2" sx={pathValueSx(theme)}>
              {path}
            </Typography>
          </Box>
        )}
      </Box>

      {args.length > 0 && (
        <Box sx={argsContainerSx}>
          <Typography variant="caption" sx={argsHeaderSx}>
            Arguments:
          </Typography>
          <Box component="ol" sx={argsListSx}>
            {args.map((arg) => (
              <Box component="li" key={arg} sx={argsListItemSx(theme)}>
                {arg}
              </Box>
            ))}
          </Box>
        </Box>
      )}
    </>
  )
}

function buildGrepArgumentsList(options: {
  caseInsensitive?: boolean | undefined
  showLineNumbers?: boolean | undefined
  contextLines?: number | undefined
  afterContextLines?: number | undefined
  beforeContextLines?: number | undefined
  multiline?: boolean | undefined
  output_mode?: string | undefined
  head_limit?: number | undefined
}): string[] {
  const args: string[] = []

  if (options.caseInsensitive !== undefined) {
    args.push(options.caseInsensitive ? 'Case insensitive' : 'Case sensitive')
  }
  if (options.showLineNumbers !== undefined) {
    args.push(options.showLineNumbers ? 'Show line numbers' : 'Hide line numbers')
  }
  if (options.contextLines !== undefined) {
    args.push(`Context lines: ${options.contextLines}`)
  }
  if (options.afterContextLines !== undefined) {
    args.push(`After context: ${options.afterContextLines}`)
  }
  if (options.beforeContextLines !== undefined) {
    args.push(`Before context: ${options.beforeContextLines}`)
  }
  if (options.multiline !== undefined) {
    args.push(options.multiline ? 'Multiline mode' : 'Single line mode')
  }
  if (options.output_mode !== undefined) {
    args.push(`Output: ${options.output_mode}`)
  }
  if (options.head_limit !== undefined) {
    args.push(`Limit: ${options.head_limit} results`)
  }

  return args
}

const argsContainerSx: SxProps<Theme> = {
  mt: 1.5,
}

const argsHeaderSx: SxProps<Theme> = {
  fontWeight: 600,
  color: 'text.secondary',
  mb: 0.5,
  fontSize: 'inherit',
}

const argsListSx: SxProps<Theme> = {
  margin: 0,
  paddingLeft: 4,
  listStyleType: 'decimal',
}

const argsListItemSx = (theme: Theme): SxProps<Theme> => ({
  color: theme.palette.text.secondary,
  paddingLeft: 0.5,
  mb: 0.25,
  fontSize: 'inherit',
})
